<template>
  <TemplateManager
    v-model:selected-template="selectedFlexRowTemplate"
    v-model:options="flexRows"
    v-model:add-template="add"
    v-model:new-template-name="newTemplateName"
    :templates="flexRowTemplates"
    :modal
    @template-changed="loadFlexRows(false)"
    @save-template="saveTemplate"
    @delete-template="deleteTemplate"
    @set-default="defaultTemplate"
    @options-changed="alphabetizeRowLists"
    @new-template="newTemplate"
    @try-confirm="tryConfirm"
  ></TemplateManager>
</template>

<script setup lang="ts">
import { useConfirm } from "primevue/useconfirm";
import { FlexRowFieldListSchema, FlexRowTemplateListSchema, type FlexRowField, type FlexRowTemplate } from "@/types/FlexRows";

const fields = ref<FlexRowField[]>([]);
const flexRows = ref<FlexRowField[][]>([]);
const flexRowTemplates = ref<FlexRowTemplate[]>([]);
const selectedFlexRowTemplate = ref<FlexRowTemplate>();
const add = ref(false);
const newTemplateName = ref<string>("");

interface Props {
  modal?: boolean;
  initialRows?: FlexRowField[];
}
const props = withDefaults(defineProps<Props>(), {
  modal: false,
});

const emit = defineEmits<{
  confirm: [customFields: FlexRowField[]];
}>();

const confirm = useConfirm();

const alphabetizeRowLists = (): void => {
  flexRows.value[0].sort((a, b) => a.label.localeCompare(b.label));
  flexRows.value[1].sort((a, b) => a.label.localeCompare(b.label));
};

onBeforeMount(async (): Promise<void> => {
  await axios
    .get("/flex-rows/fields")
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          parseData(FlexRowFieldListSchema, data, fields);
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));

  await loadFlexRowTemplates(true);
});

const loadFlexRowTemplates = async (initialization: boolean): Promise<void> => {
  await axios
    .get("/flex-rows/templates")
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          parseData(FlexRowTemplateListSchema, data, flexRowTemplates);
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));

  let previousTemplate = selectedFlexRowTemplate.value ? selectedFlexRowTemplate.value.settingId : null;

  selectedFlexRowTemplate.value = undefined;

  for (let template of flexRowTemplates.value) {
    if (previousTemplate) {
      if (template.settingId === previousTemplate) {
        selectedFlexRowTemplate.value = template;
      }
    } else if (template.defaultTemplate && props.initialRows == null) {
      selectedFlexRowTemplate.value = template;
    }
  }

  loadFlexRows(initialization);
};

const loadFlexRows = (initialization: boolean): void => {
  flexRows.value[0] = [];
  flexRows.value[1] = [];

  let templateFields: FlexRowField[] = [];
  if (props.initialRows != null && initialization) templateFields = props.initialRows;
  else if (selectedFlexRowTemplate.value != null) templateFields = selectedFlexRowTemplate.value.fields;

  for (let field of fields.value) {
    let found = false;

    for (let templateField of templateFields) {
      if (field.customField === templateField.customField && field.id === templateField.id) {
        found = true;
        flexRows.value[1].push(field);
        break;
      }
    }

    if (!found) {
      flexRows.value[0].push(field);
    }
  }

  alphabetizeRowLists();
};

const saveTemplate = (): void => {
  if (!selectedFlexRowTemplate.value) return;

  axios.post("/flex-rows/templates/update", { id: selectedFlexRowTemplate.value.settingId, included: flexRows.value[1] }).then((response) =>
    handleApiResponse(
      response,
      () => {
        loadFlexRowTemplates(false);
        success("Success", "Flex row template " + selectedFlexRowTemplate.value?.name + " updated");
      },
      (errors: ApiError[]) => {
        error("Cannot save template", errors[0].message);
      }
    )
  ).catch((e) => errorApi(e.message, e.message, e));
};

const newTemplate = (): void => {
  axios.post("/flex-rows/templates/add", { name: newTemplateName.value, included: flexRows.value[1] }).then((response) =>
    handleApiResponse(
      response,
      (data: any) => {
        selectedFlexRowTemplate.value = { settingId: data, name: newTemplateName.value, defaultTemplate: false, fields: flexRows.value[1] };
        add.value = false;
        loadFlexRowTemplates(false);
        success("Success", "Flex row template " + newTemplateName.value + " added");
        newTemplateName.value = "";
      },
      (errors: ApiError[]) => {
        error("Cannot create new template", errors[0].message);
      }
    )
  ).catch((e) => errorApi(e.message, e.message, e));
};

const deleteTemplateProcess = (): void => {
  axios.delete("/flex-rows/templates/delete/" + selectedFlexRowTemplate.value!.settingId).then((response) =>
    handleApiResponse(
      response,
      () => {
        success("Success", "Flex row template " + selectedFlexRowTemplate.value!.name + " deleted");
        selectedFlexRowTemplate.value = undefined;
        loadFlexRowTemplates(false);
      },
      (errors: ApiError[]) => {
        error("Cannot delete template", errors[0].message);
      }
    )
  ).catch((e) => errorApi(e.message, e.message, e));
};

const setDefaultTemplate = (): void => {
  if (!selectedFlexRowTemplate.value) return;

  let data = new FormData();
  data.append("id", selectedFlexRowTemplate.value?.settingId + "");

  axios.post("/flex-rows/templates/setdefault", data).then((response) =>
    handleApiResponse(
      response,
      () => {
        loadFlexRowTemplates(false);
        success("Success", "Default flex row template set to " + selectedFlexRowTemplate.value?.name);
      },
      (errors: ApiError[]) => {
        error("Cannot set default template", errors[0].message);
      }
    )
  ).catch((e) => errorApi(e.message, e.message, e));
};

const deleteTemplate = (): void => {
  if (!selectedFlexRowTemplate.value) return;

  let header = "Delete template?";
  let message = "Are you sure you want to delete the flex row template " + selectedFlexRowTemplate.value!.name + "?";
  let onReject = () => {
    error("Action cancelled", "Template not deleted");
  };

  confirmPopup(confirm, header, message, deleteTemplateProcess, onReject);
};

const defaultTemplate = (): void => {
  if (!selectedFlexRowTemplate.value) return;

  let header = "Set Default Template?";
  let message = "Are you sure you want to set the default flex row template to " + selectedFlexRowTemplate.value!.name + "?";
  let onReject = () => {
    error("Action cancelled", "Default template unchanged");
  };

  confirmPopup(confirm, header, message, setDefaultTemplate, onReject);
};

const tryConfirm = (): void => {
  confirmPopup(
    confirm,
    "Confirm Flex Rows?",
    "Are you sure that you want to confirm the selected rows?",
    () => emit("confirm", flexRows.value[1]),
    () => info("Action Cancelled", "Flex rows not confirmed")
  );
};
</script>
